import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useDeferredValue, useMemo, useState } from "react";
import { track } from "@/lib/analytics/tracker";
import { useSuspenseQuery } from "@tanstack/react-query";
import { mapDbProducts, productImageFor, publishedProductsQuery } from "@/lib/shop-catalog";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { OemHeroSearch } from "@/components/shop/OemHeroSearch";
import { NavSearch } from "@/components/shop/NavSearch";
import { ShopToolbar } from "@/components/shop/ShopToolbar";
import { FilterSidebar } from "@/components/shop/FilterSidebar";
import { ProductCard } from "@/components/shop/ProductCard";
import { ActiveFilterBar } from "@/components/shop/ActiveFilterBar";
import { EmptyState } from "@/components/shop/EmptyState";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal, ChevronLeft, ChevronRight, Truck, ShieldCheck, Search } from "lucide-react";
import type { Product } from "@/lib/catalog";
import { searchProducts, suggestNearestOem, liveSuggestMatches, looksLikeOem, type ShopFilters, type ProductMatch } from "@/lib/shop-search";

const PER_PAGE = 24;

const shopSearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  mode: fallback(z.enum(["keyword", "oem"]), "keyword").default("keyword"),
  brand: fallback(z.string(), "all").default("all"),
  models: fallback(z.string().array(), []).default([]),
  category: fallback(z.string(), "all").default("all"),
  sort: fallback(z.enum(["relevance", "price-asc", "price-desc", "newest", "rating"]), "relevance").default("relevance"),
  inStock: fallback(z.boolean(), false).default(false),
  min: fallback(z.number().min(0), 0).default(0),
  max: fallback(z.number().min(0), 0).default(0),
  page: fallback(z.number().int().min(1), 1).default(1),
});

export const Route = createFileRoute("/shop")({
  validateSearch: zodValidator(shopSearchSchema),
  loader: async ({ context }) => {
    // Prefetch on the server so AI crawlers and first-paint see the full grid.
    await context.queryClient.prefetchQuery(publishedProductsQuery);
  },
  head: () => ({
    meta: [
      { title: "Shop Classic Mercedes-Benz & BMW Parts — FIXHEN" },
      {
        name: "description",
        content:
          "OEM & quality-matched parts for Mercedes R129, W124, W201, W208 and BMW E30–E46. Search by OEM number. Free EU shipping over €99.",
      },
      { property: "og:title", content: "Shop Parts — FIXHEN" },
      {
        property: "og:description",
        content: "Search by part name, model, or OEM number across our full Mercedes-Benz and BMW catalogue.",
      },
      { property: "og:url", content: "https://fixhen.com/shop" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://fixhen.com/og/shop.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://fixhen.com/og/shop.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://fixhen.com/shop" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "/" },
                { "@type": "ListItem", position: 2, name: "Shop", item: "/shop" },
              ],
            },
            {
              "@type": "CollectionPage",
              name: "FIXHEN Parts Catalogue",
              description:
                "OEM and quality-matched parts for classic Mercedes-Benz and BMW chassis. Search by keyword or OEM number.",
              url: "/shop",
            },
          ],
        }),
      },
    ],
  }),
  component: ShopPage,
});

function ShopPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Lock page scrolling behind the mobile search overlay.
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileSearchOpen]);

  const { data: dbProducts } = useSuspenseQuery(publishedProductsQuery);

  const catalog = useMemo<Product[]>(() => mapDbProducts(dbProducts), [dbProducts]);

  const filters: ShopFilters = {
    q: search.q,
    mode: search.mode,
    brand: search.brand,
    models: search.models,
    category: search.category,
    sort: search.sort,
    inStock: search.inStock,
    min: search.min,
    max: search.max,
  };

  const deferredQ = useDeferredValue(search.q);

  const allMatches = useMemo(
    () => searchProducts(catalog, { ...filters, q: deferredQ }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, deferredQ, search.mode, search.brand, search.models.join(","), search.category, search.sort, search.inStock, search.min, search.max],
  );

  // Record every settled search with its result count — zero-result queries
  // are the clearest catalogue gap signal for the visit analysis.
  useEffect(() => {
    const q = (deferredQ ?? "").trim();
    if (q.length < 2 || catalog.length === 0) return;
    const t = window.setTimeout(() => {
      track("search", {
        name: q.slice(0, 120),
        value: allMatches.length,
        props: { mode: search.mode, brand: search.brand || undefined, category: search.category || undefined, zero_results: allMatches.length === 0 },
      });
    }, 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredQ, allMatches.length, search.mode]);

  // Facets are computed on the base catalogue with the current text query but
  // ignoring brand/model/category so users see how many results each option
  // would yield.
  const baseMatches = useMemo(
    () =>
      searchProducts(catalog, {
        ...filters,
        q: deferredQ,
        brand: "all",
        models: [],
        category: "all",
        min: 0,
        max: 0,
        inStock: false,
      }),
    [catalog, deferredQ, search.mode],
  );

  const facets = useMemo(() => {
    const byCategory: Record<string, number> = {};
    const byModel: Record<string, number> = {};
    const byBrand: Record<string, number> = {};
    let maxPrice = 0;
    for (const { product: p } of baseMatches) {
      byCategory[p.category] = (byCategory[p.category] ?? 0) + 1;
      byBrand[p.brand] = (byBrand[p.brand] ?? 0) + 1;
      for (const m of p.models) byModel[m] = (byModel[m] ?? 0) + 1;
      if (p.price > maxPrice) maxPrice = p.price;
    }
    return { byCategory, byModel, byBrand, maxPrice };
  }, [baseMatches]);

  // Models that have at least one published product in the whole catalog.
  // Derived live, so a newly published product instantly exposes its models.
  const availableModels = useMemo(() => {
    const s = new Set<string>();
    for (const p of catalog) for (const m of p.models) s.add(m);
    return s;
  }, [catalog]);

  const totalPages = Math.max(1, Math.ceil(allMatches.length / PER_PAGE));
  const currentPage = Math.min(search.page, totalPages);
  const pageMatches = allMatches.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const suggestions = useMemo(
    () => (allMatches.length === 0 && search.q && search.mode === "oem" ? suggestNearestOem(catalog, search.q, 5) : []),
    [allMatches.length, search.q, search.mode, catalog],
  );

  // Live dropdown suggestions: search in BOTH modes while typing so SKU/OEM
  // codes and plain keywords resolve without the user touching the toggle.
  const qTrim = deferredQ.trim();
  const oemLike = looksLikeOem(qTrim);
  const liveSuggest = useMemo(() => liveSuggestMatches(catalog, qTrim), [catalog, qTrim]);

  const patch = (p: Partial<ShopFilters & { page: number }>) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...p,
        page: "page" in p ? (p.page as number) : 1,
      }),
    });
  };

  const clearAll = () =>
    navigate({
      search: () => ({
        q: "",
        mode: "keyword" as const,
        brand: "all" as const,
        models: [],
        category: "all",
        sort: "relevance" as const,
        inStock: false,
        min: 0,
        max: 0,
        page: 1,
      }),
    });

  const activeCount =
    (filters.brand !== "all" ? 1 : 0) +
    filters.models.length +
    (filters.category !== "all" ? 1 : 0) +
    (filters.inStock ? 1 : 0) +
    (filters.min > 0 ? 1 : 0) +
    (filters.max > 0 ? 1 : 0);

  return (
    <div className="bg-background text-foreground">
      <AnnouncementBar />
      <SiteHeader />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-4 pt-4 text-xs text-muted-foreground sm:px-6">
        <ol className="flex items-center gap-1.5">
          <li><Link to="/" className="hover:text-ink">Home</Link></li>
          <li aria-hidden>/</li>
          <li className="font-bold text-ink">Shop</li>
        </ol>
      </nav>

      {/* Compact mobile search: tapping the field opens a navbar-style overlay
          — input pinned at the top of the screen, the two live product
          suggestions directly below, dimmed page behind. */}
      <div className="relative isolate mt-3 bg-hero text-paper md:hidden">
        <div className="bg-grid absolute inset-0 opacity-[0.18]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 pb-4 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-pop">Parts catalogue</p>
          <h1 className="mt-0.5 text-xl font-black leading-tight tracking-tight">
            Find the exact part. <span className="text-gradient-brand">Fast.</span>
          </h1>
          <button
            type="button"
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Search parts by name, SKU or OEM number"
            className="press mt-2.5 flex h-11 w-full items-center gap-2 rounded-xl border border-white/15 bg-paper px-3 text-left text-sm text-ink shadow-elevated"
          >
            <Search className="h-4 w-4 shrink-0 text-ink/40" />
            <span className={`truncate ${search.q.trim() ? "font-medium text-ink" : "text-ink/40"}`}>
              {search.q.trim() ? search.q : "Search by part, SKU or OEM…"}
            </span>
          </button>
          <p className="mt-2 text-[11px] text-paper/70" aria-live="polite">
            <span className="font-bold text-sky-pop">{allMatches.length}</span>{" "}
            {allMatches.length === 1 ? "part matches" : "parts match"}
            {search.q.trim() ? <> &ldquo;{search.q}&rdquo;</> : " · full catalogue"}
          </p>
        </div>
      </div>

      {mobileSearchOpen && (
          <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true" aria-label="Search parts">
            <div
              className="absolute inset-0 bg-ink/60 backdrop-blur-[2px]"
              onClick={() => setMobileSearchOpen(false)}
              aria-hidden
            />
            <div className="relative bg-hero pb-3 pt-3 shadow-elevated">
              <div className="bg-grid absolute inset-0 opacity-[0.18]" aria-hidden />
              <div className="relative mx-auto flex max-w-5xl items-center gap-2 px-4">
                <div className="min-w-0 flex-1">
                  <NavSearch
                    key={`overlay-${search.q}|${search.mode}`}
                    autoFocus
                    initialValue={search.q}
                    inputClassName="h-11 w-full appearance-none rounded-xl border border-white/15 bg-paper pl-9 pr-8 text-sm text-ink shadow-elevated outline-none placeholder:text-ink/40 focus:border-sky-pop"
                    onNavigate={() => {
                      setMobileSearchOpen(false);
                      requestAnimationFrame(() =>
                        document.getElementById("shop-results-heading")?.scrollIntoView({ behavior: "smooth", block: "start" }),
                      );
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setMobileSearchOpen(false)}
                  className="press grid h-11 shrink-0 place-items-center rounded-xl border border-white/15 px-3 text-xs font-bold uppercase tracking-wide text-paper/90 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      <div className="mt-4 hidden md:block">
        <OemHeroSearch
          value={search.q}
          mode={search.mode}
          resultCount={allMatches.length}
          onChange={(v) => patch({ q: v })}
          onModeChange={(m) => patch({ mode: m })}
          suggestions={liveSuggest.items}
          suggestTotal={liveSuggest.total}
          suggestOemLike={oemLike}
          productImageFor={productImageFor}
          onPickSuggestion={(m) => {
            if (m.product.slug) navigate({ to: "/p/$slug", params: { slug: m.product.slug } });
          }}
          onShowMore={() => {
            document.getElementById("shop-results-heading")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      </div>

      <ShopToolbar
        filters={filters}
        resultCount={allMatches.length}
        onPatch={patch}
        onClear={clearAll}
        onOpenFilters={() => setMobileFiltersOpen(true)}
        activeCount={activeCount}
        availableModels={availableModels}
      />

      <main className="mx-auto grid max-w-7xl gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[280px_1fr] lg:py-8">
        {/* Desktop sidebar */}
        <div className="hidden lg:block">
          <div className="sticky top-44">
            <h2 className="sr-only">Filter parts</h2>
            <FilterSidebar filters={filters} onPatch={patch} facets={facets} availableModels={availableModels} />
          </div>
        </div>


        {/* Mobile sidebar in a Sheet */}
        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <SheetTrigger className="hidden" aria-hidden />
          <SheetContent side="bottom" className="h-[85vh] overflow-y-auto rounded-t-3xl">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5" /> Filters
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <FilterSidebar filters={filters} onPatch={patch} facets={facets} availableModels={availableModels} />
            </div>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="press mt-6 inline-flex h-12 w-full items-center justify-center rounded-xl bg-ink text-sm font-bold text-paper"
            >
              Show {allMatches.length} results
            </button>
          </SheetContent>
        </Sheet>

        <section aria-labelledby="shop-results-heading">
          <h2 id="shop-results-heading" className="mb-3 text-lg font-black tracking-tight text-ink">
            {allMatches.length} part{allMatches.length === 1 ? "" : "s"} available
          </h2>
          <ActiveFilterBar filters={filters} onPatch={patch} />


          {pageMatches.length === 0 ? (
            <EmptyState
              query={search.q}
              mode={search.mode}
              suggestions={suggestions}
              onPickSuggestion={(oem) => patch({ q: oem, mode: "oem" })}
              onReset={clearAll}
              productImageFor={productImageFor}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageMatches.map((m) => (
                  <ProductCard key={m.product.id} match={m} productImageFor={productImageFor} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Page <span className="font-bold text-ink">{currentPage}</span> of {totalPages} ·{" "}
                    {allMatches.length} results
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => patch({ page: Math.max(1, currentPage - 1) })}
                      disabled={currentPage === 1}
                      className="press inline-flex h-10 items-center gap-1 rounded-xl border border-border bg-background px-3.5 text-sm font-bold text-ink disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </button>
                    <button
                      onClick={() => patch({ page: Math.min(totalPages, currentPage + 1) })}
                      disabled={currentPage === totalPages}
                      className="press inline-flex h-10 items-center gap-1 rounded-xl bg-ink px-3.5 text-sm font-bold text-paper disabled:opacity-40"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Reassurance row */}
          <div className="mt-10 grid gap-3 rounded-2xl border border-border bg-secondary/40 p-4 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-ink text-sky-pop"><Truck className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-bold text-ink">Free worldwide shipping over €99</p>
                <p className="text-xs text-muted-foreground">Tracked delivery to every country, every zone.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-ink text-sky-pop"><ShieldCheck className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-bold text-ink">14-day returns · fitment guarantee</p>
                <p className="text-xs text-muted-foreground">OEM-verified fitment on every part in the catalogue.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}